import React from 'react';
import { View } from 'react-native';
import { Feather } from '@expo/vector-icons';

import Text from '../../../components/text/Text';
import Card from '../../../design/components/Card';
import { useTheme } from '../../../theme';
import { useThemedStyles } from '../../../hooks/useThemedStyles';

/**
 * Today's weather on Home. Props: weather (parseWeatherForecast result), placeLabel.
 * Renders nothing without a forecast.
 */
const WeatherCard = ({ weather, placeLabel }) => {
	const styles = useThemedStyles(createStyles);
	const { theme: { ui: { colors } } } = useTheme();
	if (!weather) {
		return null;
	}

	// Non-breaking spaces keep "Low 15°" together when the line wraps.
	const details = [
		placeLabel,
		weather.high !== null ? `High\u00a0${weather.high}°` : null,
		weather.low !== null ? `Low\u00a0${weather.low}°` : null,
	].filter(Boolean).join(' · ');

	return (
		<Card style={styles.card} accessibilityLabel={`${weather.temperature} degrees, ${weather.label}`}>
			<Feather name={weather.icon} size={44} color={colors.accent} />
			<View style={styles.main}>
				<View style={styles.temperatureRow}>
					<Text style={styles.temperature}>{`${weather.temperature}°`}</Text>
					<Text style={styles.condition} numberOfLines={1}>{weather.label}</Text>
				</View>
				{Boolean(details) && <Text style={styles.details} numberOfLines={2}>{details}</Text>}
			</View>
			{
				weather.rainChance !== null && (
					<View style={styles.rain}>
						<Text style={styles.rainValue}>{`${weather.rainChance}%`}</Text>
						<Text style={styles.rainLabel}>chance of rain</Text>
					</View>
				)
			}
		</Card>
	);
};

const createStyles = ({ ui: { colors, type, space, radius } }) => ({
	card: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: space.lg,
		borderRadius: radius.hero,
	},
	main: {
		flex: 1,
		minWidth: 0,
		gap: space.xxs,
	},
	temperatureRow: {
		flexDirection: 'row',
		alignItems: 'baseline',
		gap: 10,
	},
	temperature: {
		...type.hero,
		color: colors.text,
	},
	condition: {
		...type.bodyStrong,
		color: colors.text,
		flexShrink: 1,
	},
	details: {
		...type.secondary,
		color: colors.textSecondary,
	},
	rain: {
		alignItems: 'flex-end',
		gap: space.xxs,
	},
	rainValue: {
		...type.cardTitle,
		color: colors.accent,
	},
	rainLabel: {
		...type.caption,
		color: colors.textSecondary,
	},
});

export default WeatherCard;
